// @flow
import * as React from 'react';
import Checkbox from 'react-bootstrap/lib/Checkbox';
import escapeRegExp from 'lodash/escapeRegExp';
import Facet from './Facet';
import { faSearch } from '@fortawesome/pro-solid-svg-icons/faSearch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';
import FormControl from 'react-bootstrap/lib/FormControl';
import FormGroup from 'react-bootstrap/lib/FormGroup';
import { getCategoryFacetLink } from '../../utils/urls';
import InputGroup from 'react-bootstrap/lib/InputGroup';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { modifyParamsRemovePage } from '../../utils/queryParams';
import noop from '../../utils/noop';
import ScrollBar from '../../components/ScrollBar/ScrollBar';
import SeeMoreItems from '../../components/SeeMoreItems/SeeMoreItems';
import ShouldLogInContainer from '../../components/ShouldLogIn/ShouldLogInContainer';
import styles from './MultiCheckFacet.module.scss';

const MAX_HEIGHT_FOR_CHECKS = 400;
const MULTISELECT_FACET_LIMIT = 7;
const MAX_HEIGHT_FOR_SEE_MORE = 325;
const excludeMessageId = 'search_facet_add_exclusion';
const includeMessageId = 'search_facet_remove_exclusion';

type Props = {
    checklist: any[],
    emptyMessage: React.Node,
    excludable?: boolean,
    facetId: string,
    facetOptionChecked: Function,
    facetOptionExcluded: Function,
    forceEnabled: boolean,
    loadPageWithFacetCollapsed?: boolean,
    queryParams: any,
    searchPlaceholder?: string,
    showCounts: boolean,
    showLandingPageLinks?: boolean,
    showSearchHeader: boolean,
    title: string,
};

type State = {
    filteredList: any[],
    searchText: string,
    showScrollBar: boolean,
};

export default class MultiCheckFacet extends React.Component<Props, State> {
    static defaultProps = {
        excludable: false,
        forceEnabled: false,
        loadPageWithFacetCollapsed: false,
        showCounts: true,
        showLandingPageLinks: false,
    };

    state = {
        filteredList: this.props.checklist,
        searchText: '',
        showScrollBar: false,
    };

    shouldComponentUpdate = (nextProps: Props, nextState: State) =>
        !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);

    componentDidUpdate(prevProps: Props) {
        if (prevProps.checklist.length !== this.props.checklist.length) {
            const showScrollBar = false;
            this.setState({ filteredList: this.props.checklist, searchText: '', showScrollBar });
        } else if (this.state.searchText !== '') {
            const filteredList = this.filterList(this.state.searchText);
            this.setState({ filteredList });
        } else {
            this.setState({ filteredList: this.props.checklist });
        }
    }

    onSeeMoreItemsClick = () => {
        const showScrollBar = !this.state.showScrollBar;
        this.setState({ showScrollBar });
    };

    filterList = (searchText: string) => {
        const search = new RegExp(`(?:^|\\W)${escapeRegExp(searchText)}`, 'i');
        // $FlowFixMe
        return this.props.checklist.filter(checkListItem => search.test(checkListItem.name));
    };

    filterListEvent = (event: SyntheticInputEvent<>) => {
        const filteredList = this.filterList(event.target.value);
        this.setState({ filteredList, searchText: event.target.value });
    };

    // "onclick"
    check = (event: SyntheticInputEvent<>) => {
        const id = event.target.value;
        this.props.facetOptionChecked(this.props.facetId, id, event.target.checked);
    };

    toggleExclude = ([optionId, excluded]: any) => {
        this.props.facetOptionExcluded(this.props.facetId, optionId, excluded);
    };

    mapHyperLinkFilteredList = ({ count, excluded, id, landingPageUrl, name, selected }: any) => {
        let { queryParams } = this.props;
        queryParams = modifyParamsRemovePage(queryParams);
        const disabled = count === 0 || excluded;
        const excludable = this.props.excludable && !selected;
        const showCounts = this.props.showCounts && !excluded;

        const classNames = [
            styles['facet-option'],
            excludable ? styles['excludable'] : '',
            excluded ? styles['excluded'] : '',
        ].join(' ');

        const resultsCountNode = showCounts && (
            <span className={`${styles['option-results-count']} results-count`}>
                ({Number(count).toLocaleString()})
            </span>
        );

        const excludeLinkNode = excludable && (
            <ShouldLogInContainer runFunction={this.toggleExclude} runFunctionArgument={[id, !excluded]} requireLogin>
                <Link className={styles['option-exclude-link']}>
                    <FormattedMessage id={excluded ? includeMessageId : excludeMessageId} />
                </Link>
            </ShouldLogInContainer>
        );

        return (
            <div key={name} className={classNames}>
                <Link
                    className={styles['option-link']}
                    key={id}
                    route
                    to={getCategoryFacetLink(landingPageUrl, queryParams)}
                >
                    <Checkbox
                        checked={selected}
                        className={styles['option-checkbox']}
                        disabled={disabled}
                        key={id}
                        value={id}
                        onChange={noop}
                    >
                        <span className={styles['option-name']}>{name}</span>
                        {resultsCountNode}
                    </Checkbox>
                </Link>
                {excludeLinkNode}
            </div>
        );
    };

    mapFilteredList = ({ count, excluded, id, name, selected, value }: any) => {
        let { showCounts, forceEnabled } = this.props;
        const disabled = (count === 0 || excluded) && !forceEnabled;
        const excludable = this.props.excludable && !selected;

        const classNames = [
            styles['facet-option'],
            excludable ? styles['excludable'] : '',
            excluded ? styles['excluded'] : '',
        ].join(' ');

        const resultsCountNode = showCounts && (
            <span className={`${styles['option-results-count']} results-count`}>
                ({Number(count).toLocaleString()})
            </span>
        );

        const excludeLinkNode = excludable && (
            <ShouldLogInContainer runFunction={this.toggleExclude} runFunctionArgument={[id, !excluded]} requireLogin>
                <Link className={styles['option-exclude-link']}>
                    <FormattedMessage id={excluded ? includeMessageId : excludeMessageId} />
                </Link>
            </ShouldLogInContainer>
        );

        // TODO: remove the `id || value` constructs, and fix the `auctionCalendar.js` module
        // to use the data structure patterns from `search.js` for consistency. (see user.js)
        return (
            <div key={name} className={classNames}>
                <Checkbox
                    checked={selected}
                    className={styles['option-checkbox']}
                    disabled={disabled}
                    key={id || value}
                    onChange={this.check}
                    value={id || value}
                >
                    <span className={styles['option-name']}>{name}</span>
                    {name === 'Top Rated' && (
                        <img
                            className={styles.topImage}
                            src="https://p1.liveauctioneers.com/dist/images/badge.svg"
                            alt="badge"
                        />
                    )}
                    {resultsCountNode}
                </Checkbox>
                {excludeLinkNode}
            </div>
        );
    };

    render() {
        const {
            emptyMessage,
            searchPlaceholder,
            showLandingPageLinks,
            showSearchHeader,
            loadPageWithFacetCollapsed,
            title = '',
        } = this.props;

        const { filteredList, showScrollBar } = this.state;
        const list = [];
        if (filteredList.length === 0) {
            list.push(emptyMessage);
        } else {
            for (let i = 0; i < filteredList.length; i += 1) {
                list.push(
                    filteredList[i].landingPageUrl && showLandingPageLinks
                        ? this.mapHyperLinkFilteredList(filteredList[i])
                        : this.mapFilteredList(filteredList[i])
                );
            }
        }
        const placeholderText = searchPlaceholder || `Search ${title}s`;
        const search = showSearchHeader && (
            <FormGroup className={styles['search']}>
                <InputGroup>
                    <FormControl
                        type="text"
                        placeholder={placeholderText}
                        onChange={this.filterListEvent}
                        value={this.state.searchText}
                    />
                    <InputGroup.Addon className={styles.icon}>
                        <FontAwesomeIcon className={styles.search} icon={faSearch} />
                    </InputGroup.Addon>
                </InputGroup>
            </FormGroup>
        );

        const maxHeight = showScrollBar ? MAX_HEIGHT_FOR_SEE_MORE : MAX_HEIGHT_FOR_CHECKS;
        const scrollBarStyle = [styles['facet-options'], showScrollBar ? styles['scrolling'] : ''].join(' ');
        const facetClass = title
            .trim()
            .replace('/', '')
            .replace(/\s+/g, '-')
            .toLowerCase();

        return (
            <Facet
                className={`multicheck-facet ${facetClass}`}
                title={title}
                header={search}
                closeFacet={loadPageWithFacetCollapsed}
            >
                <div className={styles['facet-options-offset-wrapper']}>
                    <ScrollBar className={scrollBarStyle} maxHeight={maxHeight} showScrollBar={showScrollBar}>
                        <SeeMoreItems
                            hideSeeLess
                            linkClassName={styles['see-more-items-link']}
                            items={list}
                            showItemCount={MULTISELECT_FACET_LIMIT}
                            onSeeMoreItemsClick={this.onSeeMoreItemsClick}
                        />
                    </ScrollBar>
                </div>
            </Facet>
        );
    }
}
